import { FC, useMemo } from 'react';
import { Menu, Button } from 'react-daisyui';
import { cn } from '@/utils/classNames.ts';
import { PropsWithClassName } from '@/types/PropsWithClassName';
import { useAuthContext } from '@/context/AuthContext.tsx';
import { useTranslation } from 'react-i18next';
import { adminNavData, familyMemberNavData, businessProviderNavData } from '@/layouts/Default/config-nav.tsx';
import { usePathname } from '@/hooks/usePathname.ts';
import { Link } from 'react-router-dom';
import { ArrowRightOnRectangleIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';
import useIntroCallRequests from '@/api/useIntroCallRequests.ts';
import useBookingRequests from '@/api/useBookingRequests.ts';
import useConversations from '@/api/useConversations.ts';
import Routes from '@/router/Routes.ts';

type NavbarProps = {
  onClick?: () => void;
  menuClassName?: string;
};

const Navbar: FC<PropsWithClassName<NavbarProps>> = ({ className, onClick, menuClassName }) => {
  const { session, logout, currentUser } = useAuthContext();
  const { t } = useTranslation();
  const pathname = usePathname();

  // Get user role
  const userRole = currentUser?.role;
  const isProvider = userRole === 'business_provider';

  const { data: introCallRequests } = useIntroCallRequests();
  const { data: bookingRequests } = useBookingRequests();
  const { data: conversations } = useConversations();

  const pendingIntroCalls = useMemo(() =>
    isProvider ? (introCallRequests || []).filter(r => r.status === 'pending' && !r.is_requester).length : 0,
    [introCallRequests, isProvider]
  );

  const pendingBookings = useMemo(() =>
    isProvider ? (bookingRequests || []).filter(r => r.status === 'pending' && !r.is_requester).length : 0,
    [bookingRequests, isProvider]
  );

  const unreadMessages = useMemo(() =>
    (conversations || []).reduce((sum, c) => sum + (c.unread_count || 0), 0),
    [conversations]
  );

  const badgeMap: Record<string, number> = {
    [Routes.INTRO_CALLS]: pendingIntroCalls,
    [Routes.BOOKING]: pendingBookings,
    [Routes.MESSAGES]: unreadMessages,
  };

  // Select navigation based on user role
  const navData = useMemo(() => {
    if (!userRole) return familyMemberNavData;
    if (userRole === 'admin') return adminNavData;
    if (userRole === 'business_provider') return businessProviderNavData;
    return familyMemberNavData;
  }, [userRole]);
  
  const getRoleDisplayName = (role: string | null | undefined) => {
    if (!role) return 'No role';
    if (role === 'admin') return 'Admin';
    if (role === 'business_provider') return 'Business Provider';
    if (role === 'family_member') return 'Family Member';
    return role;
  };

  // Get user display name
  const fullName = [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(' ');
  const userName = fullName || session?.user?.email?.split('@')[0] || 'User';
  const userEmail = session?.user?.email || '';

  return (
    <nav className={cn('flex flex-col gap-4 grow', className)}>
      <div className="flex flex-col grow gap-6">
        <Menu className={cn('p-0 overflow-auto flex-nowrap grow h-1', menuClassName)}>
          {navData.map((item) => {
            // Check if current path matches the nav item path
            // For paths that have subpages (like /messages/:userId), also check if pathname starts with the base path
            const isExactMatch = item.path === pathname;
            const isSubpath = pathname.startsWith(item.path + '/');
            const isActivated = isExactMatch || isSubpath;
            const isSettings = item.title === 'navbar.settings';

            return (
              <Menu.Item key={item.path}>
                <Link
                  to={item.path}
                  title={t(item.title)}
                  onClick={() => (onClick ? onClick() : null)}
                  data-active={isActivated ? 'true' : 'false'}
                  className={cn(
                    'group active:bg-black active:bg-opacity-40 hover:bg-black hover:bg-opacity-20 hover:text-[white] flex gap-2',
                    {
                      'bg-black bg-opacity-40 text-[white]': isActivated,
                    }
                  )}
                >
                  <div className="flex flex-col gap-0 w-full">
                    <div className="flex gap-2 items-center justify-between">
                      <div className="flex gap-2 items-center">
                        {item.icon ? <div className="w-4 h-4">{item.icon}</div> : null}
                        <span className="text-ellipsis line-clamp-1">{t(item.title)}</span>
                      </div>
                      {badgeMap[item.path] > 0 && (
                        <span className="bg-black text-white text-[10px] font-semibold rounded-full w-5 h-5 flex items-center justify-center shrink-0">
                          {badgeMap[item.path]}
                        </span>
                      )}
                    </div>
                    {isSettings && userRole && (
                      <span className="text-xs opacity-70 ml-6">
                        {getRoleDisplayName(userRole)}
                      </span>
                    )}
                  </div>
                </Link>
              </Menu.Item>
            );
          })}
        </Menu>
      </div>
      
      {/* User Info & Logout Section */}
      <div className="border-t border-black border-opacity-10 pt-4 pb-2 px-2">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-black bg-opacity-20 flex items-center justify-center text-black font-semibold">
            {userName.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1">
              <p className="text-sm font-semibold text-black truncate">{userName}</p>
              {userRole === 'business_provider' && (
                currentUser?.is_verified
                  ? <CheckBadgeIcon className="w-4 h-4 text-green-600 shrink-0" title={t('navbar.verified')} />
                  : <CheckBadgeIcon className="w-4 h-4 text-red-500 shrink-0" title={t('navbar.notVerified')} />
              )}
            </div>
            <p className="text-xs text-black opacity-60 truncate">{userEmail}</p>
            {userRole === 'business_provider' && (
              currentUser?.is_verified
                ? <p className="text-xs text-green-600 font-medium">{t('navbar.verified')}</p>
                : <p className="text-xs text-red-500 font-medium">{t('navbar.notVerified')}</p>
            )}
          </div>
        </div>
        <Button
          onClick={() => {
            logout();
            if (onClick) onClick();
          }}
          className="w-full bg-black bg-opacity-10 hover:bg-opacity-20 text-black border-none flex items-center justify-center gap-2"
          size="sm"
        >
          <ArrowRightOnRectangleIcon className="w-4 h-4" />
          <span>{t('button.logout')}</span>
        </Button>
      </div>
    </nav>
  );
};

export default Navbar;
